import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../hooks/AuthContext';
import { 
  Heart, 
  Home,
  TrendingUp,
  CheckCircle,
  BookOpen
} from 'lucide-react';

type PromptType = 'morning' | 'evening';

interface ReflectionDetail {
  question: string;
  answer: string | string[];
}

interface ResultsState {
  mood: number;
  responses: number;
  reflectionDetails?: ReflectionDetail[];
  promptType?: PromptType;
}

// Confetti effect removed

const MORNING_QUESTION_LABELS: Record<string, string> = {
  gratitude: '3 Things I am Grateful For',
  mini_goals: '3 Mini Goals for Making Today Great',
  intention: "Today's Intention"
};

const EVENING_QUESTION_LABELS: Record<string, string> = {
  awesome_things: '3 Awesome Things that Happened Today',
  goal_improvement: 'How can I improve tomorrow?',
  looking_forward: 'What am I looking forward to tomorrow?'
};

const mapResponsesToReflectionDetails = (
  responses: Array<{ questionId: string; value: string | string[] | number }> = [],
  promptType: PromptType
): ReflectionDetail[] => {
  const labels = promptType === 'morning' ? MORNING_QUESTION_LABELS : EVENING_QUESTION_LABELS;
  return responses.map(response => ({
    question: labels[response.questionId] || response.questionId,
    answer: Array.isArray(response.value) ? response.value : String(response.value ?? '')
  }));
};

const formatAnswer = (answer: string | string[]): string => {
  if (Array.isArray(answer)) {
    return answer.join(', ');
  }
  return answer;
};

export const JournalResults: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const [showConfetti, setShowConfetti] = useState(false);
  const [loading, setLoading] = useState(false);
  const [promptedJournal, setPromptedJournal] = useState<any>(null);
  
  const state = location.state as ResultsState | undefined;
  
  // Confetti effect removed

  // Handle prompted journal entries
  useEffect(() => {
    if (id && user) {
      fetchPromptedJournal();
    }
  }, [id, user]);

  const fetchPromptedJournal = async () => {
    if (!id || !user) return;
    
    setLoading(true);
    try {
      const docRef = doc(db, 'users', user.uid, 'promptedJournals', id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        setPromptedJournal({
          ...data,
          id: docSnap.id,
          completedAt: data.completedAt?.toDate?.() ?? data.completedAt,
          createdAt: data.createdAt?.toDate?.() ?? data.createdAt,
          updatedAt: data.updatedAt?.toDate?.() ?? data.updatedAt
        });
      } else {
        navigate('/journal');
      }
    } catch (error) {
      console.error('Error fetching prompted journal:', error);
      navigate('/journal');
    } finally {
      setLoading(false);
    }
  };

  // If loading prompted journal
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="bg-gradient-to-br from-indigo-500 to-purple-500 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto shadow-2xl animate-float mb-6">
            <BookOpen className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Loading Your Reflection</h2>
          <p className="text-gray-600">Retrieving your journal entry...</p>
        </div>
      </div>
    );
  }

  // Helper functions
  const getMoodEmoji = (mood: number) => {
    if (mood >= 8) return '😊';
    if (mood >= 6) return '🙂';
    if (mood >= 4) return '😐';
    if (mood >= 2) return '😟';
    return '😢';
  };

  const getMoodColor = (mood: number) => {
    if (mood >= 8) return 'from-green-400 to-emerald-500';
    if (mood >= 6) return 'from-blue-400 to-blue-500';
    if (mood >= 4) return 'from-yellow-400 to-orange-500';
    return 'from-red-400 to-pink-500';
  };

  // Original logic for state-based results
  if (!state && !id) {
    navigate('/journal');
    return null;
  }

  const moodFromState = state?.mood ?? 0;
  const responsesFromState = state?.responses ?? 0;
  const promptTypeFromState: PromptType = state?.promptType
    || (location.pathname.includes('morning') ? 'morning' : 'evening');
  const reflectionDetailsFromState = state?.reflectionDetails ?? [];

  const renderResultView = (data: {
    mood: number;
    responses: number;
    promptType: PromptType;
    reflectionDetails: ReflectionDetail[];
  }) => {
    const { mood, responses, promptType, reflectionDetails } = data;
    return (
      <div
        className={`min-h-screen p-3 sm:p-4 ${
          promptType === 'morning'
            ? 'bg-gradient-to-br from-yellow-50 via-orange-50 to-red-50'
            : 'bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50'
        }`}
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }}
      >
        <div className="max-w-4xl mx-auto w-full space-y-6 sm:space-y-8 px-1 sm:px-0">
          {/* Celebration Header */}
          <div className="text-center relative">
            <div className={`bg-gradient-to-br ${getMoodColor(mood)} w-20 h-20 sm:w-24 sm:h-24 rounded-3xl flex items-center justify-center mx-auto shadow-2xl mb-5 sm:mb-6 animate-float`}>
              <CheckCircle className="w-10 h-10 sm:w-12 sm:h-12 text-white" />
                </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              {promptType === 'morning' ? 'Morning' : 'Evening'} Check-in Complete! 🎉
            </h1>
            <p className="text-base sm:text-lg text-gray-600">
              Thank you for investing in your wellness. Take a moment to review your reflections below.
            </p>
              </div>
              
          {/* Mood Summary */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-4 sm:p-6 md:p-8">
            <div className="text-center mb-4 sm:mb-6">
              <div className="text-5xl sm:text-6xl mb-3 sm:mb-4">{getMoodEmoji(mood)}</div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
                Your {promptType} mood: {mood}/10
              </h2>
              <p className="text-sm sm:text-base text-gray-600">
                You completed {responses} prompts in this check-in
              </p>
            </div>
          </div>

          {/* Reflection Details */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-4 sm:p-6 md:p-8">
            <div className="flex items-center space-x-3 mb-4 sm:mb-6">
              <div className="bg-blue-100 p-2.5 sm:p-3 rounded-xl">
                <BookOpen className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                </div>
              <div>
                <h2 className="text-lg sm:text-xl font-bold text-gray-900">Today's reflections</h2>
                <p className="text-xs sm:text-sm text-gray-500">Your words, your insights</p>
              </div>
            </div>
            <div className="space-y-3">
              {reflectionDetails.map((detail, index) => (
                <div key={`${detail.question}-${index}`} className="bg-blue-50 border border-blue-100 rounded-xl p-4">
                  <h3 className="text-sm sm:text-base font-semibold text-blue-900 mb-2">{detail.question}</h3>
                  <p className="text-sm sm:text-base text-blue-800 whitespace-pre-wrap">
                    {formatAnswer(detail.answer)}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Encouragement */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-4 sm:p-6 md:p-7">
            <div className="flex items-start space-x-3">
              <div className="bg-green-500 p-2 rounded-full flex-shrink-0">
                <Heart className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <div className="min-w-0">
                <h3 className="font-semibold text-green-900 mb-1 text-sm sm:text-base">Keep reflecting</h3>
                <p className="text-xs sm:text-sm text-green-800">
                  Looking back on your own words is a powerful way to learn, grow, and stay grounded. Come back anytime to revisit how you were feeling and what mattered to you.
                </p>
              </div>
              </div>
            </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6">
            <button
              onClick={() => navigate('/')}
              className="flex items-center justify-center space-x-2 px-4 sm:px-6 py-3 sm:py-4 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-all duration-300 shadow-lg text-sm sm:text-base"
            >
              <Home className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
              <span className="font-medium text-gray-900">Back to Dashboard</span>
            </button>

            <button
              onClick={() => navigate('/journal')}
              className="flex items-center justify-center space-x-2 px-4 sm:px-6 py-3 sm:py-4 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-all duration-300 shadow-lg text-sm sm:text-base"
            >
              <BookOpen className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="font-medium">Review Journal</span>
            </button>

            <button
              onClick={() => navigate('/training')}
              className="flex items-center justify-center space-x-2 px-4 sm:px-6 py-3 sm:py-4 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-all duration-300 shadow-lg text-sm sm:text-base"
            >
              <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="font-medium">Keep Growing</span>
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (!promptedJournal && state) {
    return renderResultView({
      mood: moodFromState,
      responses: responsesFromState,
      promptType: promptTypeFromState,
      reflectionDetails: reflectionDetailsFromState
    });
  }

  if (promptedJournal) {
    const promptType: PromptType = promptedJournal.promptType || promptTypeFromState;
    const reflectionDetails = promptedJournal.reflectionDetails?.length
      ? promptedJournal.reflectionDetails
      : mapResponsesToReflectionDetails(promptedJournal.responses || [], promptType);
    const mood = promptedJournal.overallMood || moodFromState;
    const responseCount = promptedJournal.responses?.length || responsesFromState || reflectionDetails.length;

    return renderResultView({
      mood,
      responses: responseCount,
      promptType,
      reflectionDetails
    });
  }

  return renderResultView({
    mood: moodFromState,
    responses: responsesFromState,
    promptType: promptTypeFromState,
    reflectionDetails: reflectionDetailsFromState
  });
};

